import {
  PORTFOLIO_MAX_IMAGES,
  type PortfolioAdminDetail,
  type PortfolioImageInput,
  type PortfolioSaveInput,
  portfolioSaveInputSchema,
} from '@repo/api-contracts';
import { useEffect, useRef, useState } from 'react';
import { usePortfolioDetailQuery } from '@/entities/portfolio';
import { movedOrder } from '@/shared/lib';
import { usePortfolioUpload } from './use-portfolio-upload';
import { useSavePortfolio } from './use-save-portfolio';

type DraftFields = Pick<
  PortfolioSaveInput,
  'title' | 'companyIntroduction' | 'productionBackground' | 'isVisible' | 'homeSelected'
> & { clientName: string };
const fieldsFrom = (value?: PortfolioAdminDetail): DraftFields => ({
  title: value?.title ?? '',
  clientName: value?.clientName ?? '',
  companyIntroduction: value?.companyIntroduction ?? '',
  productionBackground: value?.productionBackground ?? '',
  isVisible: value?.isVisible ?? false,
  homeSelected: value?.homeSelected ?? false,
});
const countText = (value: string) => Array.from(value.replace(/\r\n/g, '\n').trim()).length;

/** 초안·순서·검증과 업로드/저장을 조정한다. 서버 재조회는 초안을 초기화하지 않는다. */
export const usePortfolioFormState = (
  initialValue: PortfolioAdminDetail | undefined,
  onSaved: () => void,
  onCancel?: () => void,
) => {
  const [original, setOriginal] = useState(initialValue);
  const [fields, setFields] = useState(() => fieldsFrom(initialValue));
  const [storedPhotos, setStoredPhotos] = useState(initialValue?.images ?? []);
  const [categoryIds, setCategoryIds] = useState(
    initialValue?.categories.map(({ id }) => id) ?? [],
  );
  const [productIds, setProductIds] = useState(initialValue?.products.map(({ id }) => id) ?? []);
  const [keepLogo, setKeepLogo] = useState(Boolean(initialValue?.logo));
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(initialValue?.brandLogoUrl ?? null);
  const [photoOrder, setPhotoOrder] = useState(initialValue?.images.map(({ id }) => id) ?? []);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const slots = useRef(initialValue?.images.length ?? 0);
  const attemptedAssetIds = useRef<string[]>([]);
  const detail = usePortfolioDetailQuery(original?.id, false);
  const save = useSavePortfolio({
    id: original?.id,
    expectedUpdatedAt: original?.updatedAt,
    onSuccess: () => {
      imageUpload.releaseSavedAssets(attemptedAssetIds.current);
      logoUpload.releaseSavedAssets(attemptedAssetIds.current);
      onSaved();
    },
  });
  const options = {
    ...(original ? {} : { createId: save.createId }),
    onCreateToken: save.setCreateToken,
  };
  const imageUpload = usePortfolioUpload('IMAGE', options);
  const logoUpload = usePortfolioUpload('LOGO', options);
  const uploadedPhotos = imageUpload.photos;
  const allPhotos = [
    ...storedPhotos.map((photo) => ({
      localId: photo.id,
      status: 'READY' as const,
      previewUrl: photo.thumbnailUrl,
      imageId: photo.id,
    })),
    ...uploadedPhotos,
  ];
  useEffect(() => {
    setPhotoOrder((current) => [
      ...current,
      ...uploadedPhotos.map(({ localId }) => localId).filter((id) => !current.includes(id)),
    ]);
  }, [uploadedPhotos]);
  const photos = [...allPhotos].sort((a, b) => {
    const first = photoOrder.indexOf(a.localId);
    const second = photoOrder.indexOf(b.localId);
    return (
      (first < 0 ? Number.MAX_SAFE_INTEGER : first) -
      (second < 0 ? Number.MAX_SAFE_INTEGER : second)
    );
  });
  const logoPhoto = logoUpload.photos[0];
  const pendingAsset =
    photos.some((photo) => photo.status !== 'READY') ||
    Boolean(logoPhoto && logoPhoto.status !== 'READY');
  const images = photos.flatMap<PortfolioImageInput>((photo) =>
    'imageId' in photo
      ? [{ kind: 'EXISTING' as const, imageId: photo.imageId }]
      : photo.assetId
        ? [{ kind: 'UPLOAD' as const, assetId: photo.assetId }]
        : [],
  );
  const input = {
    ...fields,
    images,
    categoryIds,
    productIds,
    logo: logoPhoto?.assetId
      ? { kind: 'UPLOAD' as const, assetId: logoPhoto.assetId }
      : keepLogo
        ? { kind: 'KEEP' as const }
        : null,
  };
  const parsed = portfolioSaveInputSchema.safeParse(input);
  const locked = save.isPending || save.isUncertain;
  const canSave = parsed.success && !pendingAsset && !locked && !save.isConflict;
  const fieldErrors = parsed.success
    ? {}
    : Object.fromEntries(
        parsed.error.issues.map((issue) => [String(issue.path[0]), issue.message]),
      );

  const removePhoto = (localId: string) => {
    if (locked) return;
    setStoredPhotos((current) => current.filter(({ id }) => id !== localId));
    imageUpload.remove(localId);
    setPhotoOrder((current) => current.filter((id) => id !== localId));
    slots.current = Math.max(0, slots.current - 1);
    setPhotoError(null);
  };
  const refreshUrls = async () => {
    setUrlError(null);
    try {
      const latest = await detail.fetchLatest();
      setStoredPhotos((current) =>
        current.map((photo) => latest.images.find(({ id }) => id === photo.id) ?? photo),
      );
      if (
        original?.brandLogoUrl &&
        latest.brandLogoUrl &&
        new URL(original.brandLogoUrl).pathname === new URL(latest.brandLogoUrl).pathname
      ) {
        setLogoPreviewUrl(latest.brandLogoUrl);
      } else if (original?.brandLogoUrl !== latest.brandLogoUrl) {
        setUrlError('서버에서 로고가 변경되었습니다. 최신 내용을 확인해주세요.');
      }
    } catch {
      setUrlError('이미지 주소를 갱신하지 못했습니다. 다시 시도해주세요.');
    }
  };

  return {
    original,
    fields,
    categoryIds,
    productIds,
    photos,
    logoPhoto,
    keepLogo,
    logoPreviewUrl,
    input,
    save,
    canSave,
    locked,
    fieldErrors,
    photoError,
    urlError,
    counts: {
      title: countText(fields.title),
      clientName: countText(fields.clientName),
      companyIntroduction: countText(fields.companyIntroduction),
      productionBackground: countText(fields.productionBackground),
    },
    changeField: <K extends keyof DraftFields>(key: K, value: DraftFields[K]) => {
      if (!locked) setFields((current) => ({ ...current, [key]: value }));
    },
    setCategoryIds: (ids: string[]) => {
      if (!locked) setCategoryIds(ids);
    },
    setProductIds: (ids: string[]) => {
      if (!locked) setProductIds(ids);
    },
    uploadPhotos: (files: readonly File[]) => {
      if (locked) return;
      const remaining = PORTFOLIO_MAX_IMAGES - slots.current;
      if (files.length > remaining) setPhotoError('사진은 최대 10장까지 등록할 수 있습니다.');
      const selected = files.slice(0, remaining);
      slots.current += selected.length;
      for (const file of selected) void imageUpload.upload(file);
    },
    removePhoto,
    retryPhoto: (id: string) => {
      if (!locked) void imageUpload.retry(id);
    },
    refreshPhoto: (id: string) => {
      if (storedPhotos.some((photo) => photo.id === id)) void refreshUrls();
      else if (!locked) void imageUpload.retry(id);
    },
    movePhoto: (from: number, to: number) => {
      if (!locked)
        setPhotoOrder(
          movedOrder(
            photos.map(({ localId }) => localId),
            from,
            to,
          ),
        );
    },
    representative: (id: string) => {
      if (!locked)
        setPhotoOrder([
          id,
          ...photos.map(({ localId }) => localId).filter((value) => value !== id),
        ]);
    },
    uploadLogo: (file: File) => {
      if (locked) return;
      for (const photo of logoUpload.photos) logoUpload.remove(photo.localId);
      setKeepLogo(false);
      void logoUpload.upload(file);
    },
    removeLogo: () => {
      if (!locked) {
        for (const photo of logoUpload.photos) logoUpload.remove(photo.localId);
        setKeepLogo(false);
      }
    },
    retryLogo: () => {
      if (logoPhoto && !locked) void logoUpload.retry(logoPhoto.localId);
    },
    refreshLogo: () => {
      if (logoPhoto && !locked) void logoUpload.retry(logoPhoto.localId);
      else void refreshUrls();
    },
    submit: () => {
      if (canSave && parsed.success) {
        attemptedAssetIds.current = [
          ...parsed.data.images.flatMap((photo) =>
            photo.kind === 'UPLOAD' ? [photo.assetId] : [],
          ),
          ...(parsed.data.logo?.kind === 'UPLOAD' ? [parsed.data.logo.assetId] : []),
        ];
        imageUpload.preserveOnUnmount();
        logoUpload.preserveOnUnmount();
        void save.save(parsed.data);
      }
    },
    retrySave: () => {
      if (save.isUncertain) void save.save(input);
    },
    cancel: () => {
      if (save.isPending) return;
      void imageUpload.discardNewAssets();
      void logoUpload.discardNewAssets();
      onCancel?.();
    },
    reloadLatest: () => {
      const latest = save.latest;
      if (!latest || locked) return;
      void imageUpload.discardNewAssets();
      void logoUpload.discardNewAssets();
      setOriginal(latest);
      setFields(fieldsFrom(latest));
      setStoredPhotos(latest.images);
      setCategoryIds(latest.categories.map(({ id }) => id));
      setProductIds(latest.products.map(({ id }) => id));
      setKeepLogo(Boolean(latest.logo));
      setLogoPreviewUrl(latest.brandLogoUrl);
      setPhotoOrder(latest.images.map(({ id }) => id));
      slots.current = latest.images.length;
      save.adoptBaseline(latest.updatedAt);
    },
  };
};
